<template>
  <div class="filter-data-root" :style="`max-height: calc(${h}px - ${$vuetify.breakpoint.mobile ? 28 : 48}px);`">
    <div v-if="hasActiveFilters" class="filter-status-bar">
      <v-btn
        small
        text
        color="primary"
        @click="clearFilters"
      >
        <v-icon left small>mdi-filter-remove</v-icon>
        Tyhjennä suodattimet
      </v-btn>
    </div>
    <div class="filter-stats-scroll">
      <FilterStats
        v-for="stat in statsToShow"
        :key="stat.key"
        :data="allFilteredEvents"
        :key-prop="stat.key"
        :title="stat.title"
        :mapping="stat.mapping"
        :filter-key="stat.filterKey"
        :alternative-value="stat.alternativeValue"
      />
    </div>
    <div class="map-controls" v-if="!$vuetify.breakpoint.mobile">
      <v-divider></v-divider>
      <FilterMap v-if="currentLeagueName !== 'nyp'" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import FilterStats from './FilterStats.vue';
import FilterMap from './FilterMap.vue';

export default {
  components: {
    FilterStats,
    FilterMap
  },
  props: ['h'],
  data() {
    return {
      originalFilter: {}
    }
  },
  created() {
    // Store the original filter state
    this.originalFilter = JSON.parse(JSON.stringify(this.filter));
  },
  computed: {
    ...mapGetters('filter', [
      'allFilteredEvents',
      'games',
      'filter'
    ]),
    ...mapGetters('user', [
      'currentLeagueName'
    ]),
    hasActiveFilters() {
      return Object.entries(this.filter).some(([key, value]) => {
        const originalValue = this.originalFilter[key];

        // If the original value doesn't exist, check if current value is non-default
        if (originalValue === undefined) {
          if (Array.isArray(value)) {
            return value.length > 0 && !value.includes('all');
          }
          return value !== null && value !== undefined;
        }

        // Compare current value with original value
        if (Array.isArray(value) && Array.isArray(originalValue)) {
          if (value.length !== originalValue.length) return true;
          return value.some(v => !originalValue.includes(v));
        }

        return JSON.stringify(value) !== JSON.stringify(originalValue);
      });
    },
    gameMappings() {
      let maps = {}

      this.games.forEach(g => {
        maps[g.game.home_club_id] = g.game.home_team_name
        maps[g.game.away_club_id] = g.game.away_team_name
      })

      return maps;
    },
    statsToShow() {
      return [
        {
          key: 'hitter_name',
          title: 'Lyöjä',
          filterKey: 'hitters',
          alternativeValue: 'hitter_id'
        },
        {
          key: 'karkirunner_name',
          title: 'Kärkietenijä',
          filterKey: 'runners',
          alternativeValue: 'karkirunner_id'
        },
        {
          key: 'hit_outcome',
          title: 'Lopputulos',
          filterKey: 'hit_outcome',
          mapping: {
            'palo': 'Palo',
            'haava': 'Haava',
            'karkilyonti': 'Kärkilyönti',
            'haava_kt': 'Haava kärjen takana',
            'takaeteneminen': 'Takaeteneminen',
            'takapalo': 'Takapalo',
            'vapaa': 'Vapaa'
          },
        },
        {
          key: 'outs',
          title: 'Palot',
          filterKey: 'outs'
        },
        {
          key: 'hitter_side',
          title: 'Lyöjän puoli',
          filterKey: 'hitter_side',
          mapping: {
            'L': 'Vasen',
            'R': 'Oikea'
          }
        },
        {
          key: 'hit_number',
          title: 'Lyönnin numero',
          filterKey: 'hit_number'
        },
        {
          key: 'hit_type',
          title: 'Lyönnin tyyppi',
          filterKey: 'hit_type',
          mapping: {
            'pystari/viisto': 'Pyke/viisto',
            'vaakamaila': 'Vaakamaila',
            'kumura': 'Kumura',
            'nappi': 'Näppi',
            'koppi': 'Kopinnosto',
            'pomppu': 'Pomppu',
            'pussari': 'Pussari',
            'varsi': 'Varsi',
            'huti': 'Huti',
            'muu': 'Muu'
          },
        },
        {
          key: 'marked',
          title: 'Merkattu',
          filterKey: 'marked',
          mapping: {
            true: 'Kyllä',
            false: 'Ei'
          },
        },
        {
          key: 'mark_type',
          title: 'Merkin tyyppi',
          filterKey: 'mark_type',
          mapping: {
            'lento': 'Lento / nopea',
            'vp': 'Väärä pois',
            'takamaila': 'Takamaila',
            'kulmamaila': 'Kulmamaila',
            'epaselva_maila': 'Epäselvä maila',
            'viivastetty': 'Viivästetty',
            'oma_lahto': 'Oma lähtö',
            'epaselva': 'Epäselvä'
          }
        },
        {
          key: 'vvo',
          title: 'Vääräksi vedottu oikea',
          filterKey: 'vvo',
          mapping: {
            true: 'Kyllä',
            false: 'Ei'
          },
        },
        {
          key: 'vaara_alla',
          title: 'Väärä alla',
          filterKey: 'vaara_alla',
          mapping: {
            true: 'Kyllä',
            false: 'Ei'
          },
        },
        {
          key: 'pitch.wrong_type',
          title: 'Väärän tyyppi',
          filterKey: 'wrong_type',
          mapping: {
            'matala': 'Matala',
            'lautasvaara': 'Lautasväärä',
            'linkkuvaara': 'Linkkuväärä',
            'asennoton': 'Asennoton',
            'muu': 'Muu väärä'
          }
        },
        {
          key: 'lapilyonti',
          title: 'Läpilyönti',
          filterKey: 'lapilyonti',
          mapping: {
            true: 'Kyllä',
            false: 'Ei'
          }
        },
        {
          key: 'pitch.tall_pitch',
          title: 'Korkea syöttö',
          filterKey: 'tall_pitch',
          mapping: {
            true: 'Kyllä',
            false: 'Ei'
          }
        },
        {
          key: 'hitting_team_id',
          title: 'SP-joukkue',
          filterKey: 'spClubs',
          mapping: this.gameMappings,
        },
        {
          key: 'fielding_team_id',
          title: 'UP-joukkue',
          filterKey: 'upClubs',
          mapping: this.gameMappings,
        },
        // {
        //   key: 'pitcher_name',
        //   title: 'Lukkari',
        //   filterKey: 'pitchers',
        //   alternativeValue: 'pitcher_id'
        // },
        {
          key: 'outfield_formation',
          title: 'Ulkopelikuvio',
          filterKey: 'outfield_formation',
          mapping: {
            'nolla_pertsa': 'Pertsa nolla',
            'vasuri': 'Vasuri',
            'oulu': 'Oulu',
            'kaannetty': 'Käännetty',
            'kolmospuolen_kaanto': '3-puolen kääntö',
            '1v_irti': '1-v irti',
            'normaali': 'Normaali',
            'syvapussi': 'Syvä pussi',
            'sailytys_vaihto': 'Säilytys vaihto',
            'kasari': 'Kasari',
            'nolla_vaihdossa': 'Nol. kuv. vaihtotilanteessa',
            'pertsa': 'Pertsa',
            'kaannetty_oulu': 'Käännetty oulu',
            'kaannetty_pertsa': 'Käännetty pertsa',
            'tahko': 'Tahko',
            'vasurin_tahko': 'Vasurin tahko',
            'sailytys_takanen': 'Säilytys takanen',
            'karvaus_yksi': 'Karvaus yhdellä',
            'karvaus_kaksi': 'Karvaus kahdella',
            'karvaus_ykkostilanne': 'Ykköstilannekarvaus'
          }
        },
        {
          key: 'play.play_outcome',
          title: 'Ulkopelisuorituksen lopputulos',
          filterKey: 'play_outcome',
          mapping: {
            'onnistuminen': 'Onnistuminen',
            'pakotettu_epaonnistuminen': 'Pakotettu epäonnistuminen',
            'kiinniottovirhe': 'Kiinniottovirhe',
            'kiinniottovirhe_vapaalla': 'Kiinniottovirhe vapaalla lyönnillä',
            'harhaheitto': 'Harhaheitto',
            'kiinniottovirhe_pesalla': 'Kiinniottovirhe pesällä'
          }
        },
        {
          key: 'play.play_player.name',
          title: 'Suorittava ulkopelaaja',
          filterKey: 'outfielders',
          alternativeValue: 'play.play_player.id'
        },
      ];
    }
  },
  methods: {
    ...mapMutations({
      updateFilter: 'filter/SET_CURRENT_FILTER'
    }),
    clearFilters() {
      // Revert to original filter state
      this.updateFilter(JSON.parse(JSON.stringify(this.originalFilter)));
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-data-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.filter-status-bar {
  flex-shrink: 0;
  padding: 4px 8px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.filter-stats-scroll {
  flex: 1 1 0%;
  min-height: 0;
  overflow-y: auto;
}

.map-controls {
  flex-shrink: 0;
  background: white;
}
</style>